import { useDispatch } from "react-redux";
import { useSendAudioMessageMutation } from "@store/companionApi";
import { setProcessing, setText } from "@store/avatarSlice";
import splitTextToSentences from "@utils/splitTextToSentences";
import { MutableRefObject, useCallback } from "react";
import { HeygenAvatarService } from "@service/avatar/heygen-avatar.service";
import { EventType } from '@models/audio-message';

const MAX_SENTENCE_LENGTH = 500;

const useBatching = ({
  sessionId,
  avatar,
  ttsInterruptGenerationRef,
}: {
  sessionId: string;
  avatar: MutableRefObject<HeygenAvatarService | null>;
  ttsInterruptGenerationRef?: MutableRefObject<number>;
}) => {
  const dispatch = useDispatch();
  const [sendAudioMessage] = useSendAudioMessageMutation();

  const speakSentences = useCallback(
    async (sentences: string[]) => {
      if (!avatar.current) {
        return;
      }
      const genAtStart = ttsInterruptGenerationRef?.current ?? 0;
      for (const sentence of sentences) {
        if (
          ttsInterruptGenerationRef &&
          ttsInterruptGenerationRef.current !== genAtStart
        ) {
          break;
        }
        await avatar.current.speak(sentence);
      }
    },
    [avatar, ttsInterruptGenerationRef],
  );

  const sendMessage = useCallback(
    async (
      audioBlob?: Blob | ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
    ): Promise<void> => {
      dispatch(setProcessing(true));
      try {
        const { data } = await sendAudioMessage({
          audio: (audioBlob as Blob) || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
        });

        if (!data) {
          return;
        }

        const { aiMessage } = data;

        if (!aiMessage) {
          return;
        }

        dispatch(setText(aiMessage));

        const sentences = splitTextToSentences(aiMessage, MAX_SENTENCE_LENGTH);

        await speakSentences(sentences);
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(setProcessing(false));
      }
    },
    [dispatch, sendAudioMessage, sessionId, speakSentences],
  );

  return sendMessage;
};

export default useBatching;
